import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ChevronRight, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ParticipantQuestionnaire = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [participantId, setParticipantId] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [totalScore, setTotalScore] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [participantName, setParticipantName] = useState('');
  const [nameEntered, setNameEntered] = useState(false);
  const [nameInput, setNameInput] = useState('');
  
  // Ranking state - stores which options were selected in order
  const [rankingSelections, setRankingSelections] = useState([]);
  
  // Store randomized options per question
  const [randomizedOptions, setRandomizedOptions] = useState({});

  useEffect(() => {
    // Generate or retrieve participant ID
    let pid = localStorage.getItem(`participant_${sessionId}`);
    if (!pid) {
      pid = `participant_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(`participant_${sessionId}`, pid);
    }
    setParticipantId(pid);
    
    fetchSession();
  }, [sessionId]);
  
  // Countdown timer
  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0 || completed) return;
    
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          toast.error('Time is up! Submitting your responses...');
          // Auto-submit incomplete
          setCompleted(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timeRemaining, completed]);

  const fetchSession = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}`);
      const sessionData = response.data;
      
      if (sessionData.session_type !== 'questionnaire') {
        toast.error('This is not a questionnaire session');
        setLoading(false);
        return;
      }
      
      if (sessionData.status !== 'active') {
        toast.error('This questionnaire is not currently active');
        setLoading(false);
        return;
      }
      
      setSession(sessionData);
      
      // Check if participant already completed this questionnaire
      const hasCompleted = localStorage.getItem(`questionnaire_completed_${sessionId}`);
      if (hasCompleted && !sessionData.allow_retry) {
        setAlreadyCompleted(true);
        setLoading(false);
        return;
      }
      
      // Check if name is required
      if (!sessionData.is_anonymous) {
        const savedName = localStorage.getItem(`participant_name_${sessionId}`);
        if (savedName) {
          setParticipantName(savedName);
          setNameEntered(true);
        }
        // If no saved name, nameEntered stays false and we show name entry screen
      } else {
        // Anonymous - skip name entry
        setNameEntered(true);
      }
      
      // Randomize options for questions that have randomize enabled
      const randomized = {};
      sessionData.questions.forEach(question => {
        if (question.randomize_options && question.options && question.options.length > 0) {
          // Fisher-Yates shuffle
          const shuffled = [...question.options];
          for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
          }
          randomized[question.id] = shuffled;
        } else {
          randomized[question.id] = question.options || [];
        }
      });
      setRandomizedOptions(randomized);
      
      // Check expiry
      if (sessionData.end_datetime) {
        const endTime = new Date(sessionData.end_datetime).getTime();
        const now = Date.now();
        if (now > endTime) {
          setIsExpired(true);
          setLoading(false);
          return;
        }
      }
      
      // Start timer
      if (sessionData.time_limit > 0) {
        setTimeRemaining(sessionData.time_limit);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch session:', error);
      toast.error('Failed to load questionnaire');
      setLoading(false);
    }
  };

  const handleResponse = (questionId, response) => {
    setResponses({
      ...responses,
      [questionId]: response
    });
  };

  const submitResponse = async (questionId, response) => {
    try {
      const result = await axios.post(`${API}/sessions/${sessionId}/questionnaire-response`, {
        participant_id: participantId,
        question_id: questionId,
        response: response
      });
      
      // Accumulate score if returned
      if (result.data.score !== null && result.data.score !== undefined) {
        setTotalScore(prev => prev + result.data.score);
      }
      
      return result.data;
    } catch (error) {
      console.error('Failed to submit response:', error);
      toast.error('Failed to save response');
      return null;
    }
  };

  const handleNext = async () => {
    const currentQuestion = session.questions[currentQuestionIndex];
    const response = responses[currentQuestion.id];
    
    // Validate response
    if (!response || Object.keys(response).length === 0) {
      toast.error('Please provide an answer before continuing');
      return;
    }
    
    setSubmitting(true);
    
    // Submit response to backend
    await submitResponse(currentQuestion.id, response);
    
    // Move to next question or complete
    if (currentQuestionIndex < session.questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      
      // Clear ranking selections for next question
      setRankingSelections([]);
    } else {
      setCompleted(true);
      // Mark as completed in localStorage (unless retry is allowed)
      if (!session.allow_retry) {
        localStorage.setItem(`questionnaire_completed_${sessionId}`, 'true');
      }
      toast.success('Questionnaire completed! Thank you for your responses.');
    }
    
    setSubmitting(false);
  };

  const handleRankingClick = (optionId) => {
    const currentQuestion = session.questions[currentQuestionIndex];
    
    // If already selected, remove it
    if (rankingSelections.includes(optionId)) {
      const newSelections = rankingSelections.filter(id => id !== optionId);
      setRankingSelections(newSelections);
      handleResponse(currentQuestion.id, { ranking: newSelections });
    } else {
      // Add to selections
      const newSelections = [...rankingSelections, optionId];
      setRankingSelections(newSelections);
      handleResponse(currentQuestion.id, { ranking: newSelections });
    }
  };
  
  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (!nameInput.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    const name = nameInput.trim();
    setParticipantName(name);
    localStorage.setItem(`participant_name_${sessionId}`, name);
    
    // Use name as part of participant ID
    const nameBasedId = `${name.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`;
    localStorage.setItem(`participant_${sessionId}`, nameBasedId);
    setParticipantId(nameBasedId);
    
    setNameEntered(true);
    toast.success(`Welcome, ${name}!`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading questionnaire...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="text-center p-8">
          <p className="text-gray-600 text-lg">Questionnaire not found or not available</p>
        </div>
      </div>
    );
  }
  
  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⏰</span>
            </div>
            <h2 className="text-2xl font-bold mb-2">Questionnaire Expired</h2>
            <p className="text-gray-600">
              The deadline for this questionnaire has passed. Submissions are no longer accepted.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (alreadyCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Already Completed</h2>
            <p className="text-gray-600 mb-4">
              You have already submitted your responses to this questionnaire.
            </p>
            <p className="text-sm text-gray-500">
              Retries are not allowed for this questionnaire.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Name entry screen for non-anonymous questionnaires
  if (!nameEntered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2">{session.topic}</h2>
              <p className="text-gray-600">Please enter your name to begin</p>
            </div>
            
            <form onSubmit={handleNameSubmit}>
              <div className="mb-4">
                <Label htmlFor="name">Your Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="Enter your full name"
                  className="mt-1"
                  autoFocus
                />
              </div>
              
              <Button
                type="submit"
                className="w-full"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                Start Questionnaire
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Thank You{participantName ? `, ${participantName}` : ''}!</h2>
            <p className="text-gray-600 mb-4">
              Your responses have been submitted successfully.
            </p>
            {session.show_scores && totalScore > 0 && (
              <div className="mt-6 p-4 bg-purple-50 border-2 border-purple-200 rounded-lg">
                <p className="text-sm text-purple-600 font-semibold mb-1">Your Score</p>
                <p className="text-4xl font-bold text-purple-700">{totalScore}</p>
                <p className="text-xs text-purple-600 mt-1">points</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentQuestion = session.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / session.questions.length) * 100;
  
  // Get display options (randomized if enabled, otherwise original order)
  const displayOptions = randomizedOptions[currentQuestion.id] || currentQuestion.options || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      {/* Progress Bar */}
      <div className="w-full bg-gray-200 h-2">
        <div
          className="h-full bg-gradient-to-r from-purple-600 to-blue-600 transition-all duration-300"
          style={{ width: `${progress}%` }}
        ></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
            {session.topic}
          </h1>
          <p className="text-sm text-gray-600">
            Question {currentQuestionIndex + 1} of {session.questions.length}
          </p>
          {timeRemaining !== null && timeRemaining > 0 && (
            <div className={`mt-2 inline-block px-4 py-2 rounded-full ${
              timeRemaining < 60 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
            }`}>
              <span className="font-semibold">⏱️ {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}</span>
            </div>
          )}
        </div>

        {/* Question Card */}
        <Card className="shadow-xl">
          <CardContent className="p-6 md:p-8">
            {/* Question Text */}
            <h2 className="text-xl md:text-2xl font-semibold mb-4 text-gray-800">
              {currentQuestion.text}
            </h2>

            {/* Question Image */}
            {currentQuestion.image_url && (
              <img
                src={currentQuestion.image_url}
                alt="Question"
                className="w-full rounded-lg mb-6"
              />
            )}

            {/* Response Type: Star Rating */}
            {currentQuestion.response_type === 'star_rating' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Rate from 1 to 5 stars</p>
                <div className="flex justify-center gap-1">
                  {[1, 2, 3, 4, 5].map((rating) => {
                    const isSelected = responses[currentQuestion.id]?.rating >= rating;
                    return (
                      <button
                        key={rating}
                        onClick={() => handleResponse(currentQuestion.id, { rating })}
                        className="transition-transform hover:scale-110 p-2"
                      >
                        <svg
                          className={`w-12 h-12 ${isSelected ? 'text-yellow-400' : 'text-gray-300'}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Response Type: MCQ */}
            {currentQuestion.response_type === 'mcq_response' && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Choose one option</p>
                {displayOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => handleResponse(currentQuestion.id, { answer: option.id })}
                    className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                      responses[currentQuestion.id]?.answer === option.id
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <span className="font-medium">{option.text}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Response Type: Image MCQ */}
            {currentQuestion.response_type === 'image_mcq_response' && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Choose one option</p>
                <div className="grid grid-cols-2 gap-4">
                  {displayOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleResponse(currentQuestion.id, { answer: option.id })}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        responses[currentQuestion.id]?.answer === option.id
                          ? 'border-purple-600 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      {option.image_url && (
                        <img
                          src={option.image_url}
                          alt={option.text}
                          className="w-full h-32 object-cover rounded mb-2"
                        />
                      )}
                      <span className="text-sm font-medium">{option.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Response Type: Ranking */}
            {currentQuestion.response_type === 'drag_reorder_ranking' && (
              <div className="space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800 font-medium">
                    📋 Click options in your order of preference
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    First click = Your 1st choice, Second click = Your 2nd choice, etc.
                  </p>
                </div>
                <div className="space-y-2">
                  {displayOptions.map((option) => {
                    const selectionIndex = rankingSelections.indexOf(option.id);
                    const isSelected = selectionIndex !== -1;
                    const rank = isSelected ? selectionIndex + 1 : null;
                    
                    return (
                      <button
                        key={`${option.id}-${selectionIndex}`}
                        type="button"
                        onClick={() => handleRankingClick(option.id)}
                        className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                          isSelected
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl ${
                            isSelected 
                              ? 'bg-purple-600 text-white' 
                              : 'bg-gray-200 text-gray-500'
                          }`}>
                            {rank || '?'}
                          </div>
                          <div className="flex-1">
                            <span className="font-medium text-lg">{option.text}</span>
                            {isSelected && (
                              <p className="text-xs text-purple-600 mt-1">
                                Your choice #{rank}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {rankingSelections.length > 0 && (
                  <div className="text-center mt-4 space-y-1">
                    <p className="text-sm font-medium text-gray-700">
                      Selected {rankingSelections.length} of {displayOptions.length} options
                    </p>
                    <p className="text-xs text-gray-500">
                      Click a selected option to remove it
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Next Button */}
            <div className="mt-8">
              <Button
                onClick={handleNext}
                disabled={submitting || !responses[currentQuestion.id]}
                className="w-full"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                {submitting ? 'Submitting...' : currentQuestionIndex < session.questions.length - 1 ? 'Next Question' : 'Submit'}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ParticipantQuestionnaire;
